/**
 * How Finnish municipalities spend their money, per municipality, 2025: net operating costs by
 * task (varhaiskasvatus, perusopetus, ...) as shares of the total, from the Association of
 * Finnish Municipalities' workbook "Kuntien palvelutuotannon nettokäyttökustannukset 2025"
 * (https://www.kuntaliitto.fi/sites/default/files/media/file/Kuntien%20palvelutuotannon%20nettok%C3%A4ytt%C3%B6kustannukset%202025_2.xlsx,
 * sheet Kunnittain, EUR per inhabitant; its own source is Valtiokonttori, 20.8.2026; downloaded
 * 2026-09-06, linked from https://www.kuntaliitto.fi/talous-ja-elinvoima/kuntatalouden-tilastot).
 *
 * Groups (workbook columns): early childhood + pre-primary; basic education; vocational, upper
 * secondary and other teaching; sport, youth and culture; housing, streets, rescue, parks,
 * building control, planning and environmental protection; business and employment services;
 * other task classes (incl. the small remaining social and health items). Net revenues (land
 * sales, water and energy utilities) are clamped at zero so shares describe spending only.
 * Covers the 295 mainland municipalities; the 16 Åland municipalities are not in the workbook and
 * fall back to the national profile. Generated by script from the workbook, not typed by hand. Used to split the municipal income
 * tax slice of the tax iceberg by purpose; it is a spending profile, not a statement that
 * municipal tax is earmarked (municipalities also have state grants, fees and property tax).
 */

export type MunicipalSpendingKey = 'earlyChildhood' | 'basicEducation' | 'upperSecondary' | 'cultureSport' | 'streetsParksPlanning' | 'employmentBusiness' | 'administrationOther';

export const MUNICIPAL_SPENDING_KEYS: MunicipalSpendingKey[] = ['earlyChildhood', 'basicEducation', 'upperSecondary', 'cultureSport', 'streetsParksPlanning', 'employmentBusiness', 'administrationOther'];

/** Population-weighted national profile (%), for when no municipality is chosen. */
export const MUNICIPAL_SPENDING_NATIONAL_2025: Record<MunicipalSpendingKey, number> = {earlyChildhood: 23.2, basicEducation: 35.6, upperSecondary: 8.0, cultureSport: 11.5, streetsParksPlanning: 10.2, employmentBusiness: 9.2, administrationOther: 2.4};

/** Per municipality, % of net operating costs, in the key order of MUNICIPAL_SPENDING_KEYS. */
export const MUNICIPAL_SPENDING_2025: Record<string, number[]> = {
  "Akaa": [23.2, 42.3, 3.4, 7.4, 9.2, 14.5, 0.0],
  "Alajärvi": [17.3, 41.4, 9.4, 14.3, 9.6, 5.9, 2.2],
  "Alavieska": [23.7, 49.5, 0.3, 8.2, 5.0, 6.2, 7.1],
  "Alavus": [20.9, 37.8, 6.0, 10.1, 10.6, 7.8, 6.8],
  "Asikkala": [18.3, 41.4, 9.2, 8.8, 9.4, 8.8, 4.2],
  "Askola": [19.8, 48.9, 9.3, 8.7, 5.2, 5.6, 2.5],
  "Aura": [29.9, 46.7, 0.6, 5.6, 6.9, 6.3, 3.9],
  "Enonkoski": [17.6, 37.7, 0.5, 11.0, 9.9, 9.5, 13.8],
  "Enontekiö": [14.8, 37.1, 7.4, 11.9, 5.9, 10.0, 12.9],
  "Espoo": [27.0, 36.0, 6.7, 11.9, 10.7, 7.6, 0.0],
  "Eura": [22.7, 41.6, 6.9, 10.7, 5.2, 5.8, 7.0],
  "Eurajoki": [24.3, 43.2, 5.6, 9.6, 9.5, 5.7, 2.1],
  "Evijärvi": [17.3, 48.9, 9.7, 7.6, 7.3, 4.5, 4.6],
  "Forssa": [19.0, 33.9, 7.9, 12.7, 14.3, 9.4, 2.8],
  "Haapajärvi": [23.3, 39.1, 10.2, 9.3, 8.1, 7.9, 2.2],
  "Haapavesi": [20.6, 46.4, 9.7, 9.3, 4.7, 5.0, 4.4],
  "Hailuoto": [16.6, 46.5, 0.3, 10.3, 6.5, 5.4, 14.4],
  "Halsua": [17.6, 44.8, 0.6, 9.9, 4.1, 7.2, 15.8],
  "Hamina": [19.7, 36.7, 5.2, 12.9, 10.5, 10.5, 4.4],
  "Hankasalmi": [16.9, 40.6, 10.8, 7.8, 7.4, 8.2, 8.4],
  "Hanko": [15.2, 31.8, 8.2, 13.5, 16.2, 8.2, 6.9],
  "Harjavalta": [21.3, 36.1, 5.1, 12.9, 11.1, 8.8, 4.7],
  "Hartola": [18.8, 41.8, 1.2, 11.3, 9.0, 10.1, 7.8],
  "Hattula": [24.8, 44.0, 1.5, 9.1, 8.7, 6.3, 5.7],
  "Hausjärvi": [22.7, 47.4, 4.8, 6.7, 8.0, 6.6, 3.8],
  "Heinola": [16.5, 31.4, 4.9, 12.9, 15.3, 14.9, 4.2],
  "Heinävesi": [14.4, 49.1, 5.0, 9.5, 9.1, 9.8, 3.1],
  "Helsinki": [23.2, 30.5, 11.7, 14.6, 10.4, 9.5, 0.0],
  "Hirvensalmi": [19.7, 38.5, 0.6, 7.1, 14.4, 9.6, 10.1],
  "Hollola": [25.8, 46.8, 0.3, 6.0, 7.7, 7.9, 5.6],
  "Huittinen": [22.4, 35.9, 8.8, 10.8, 9.4, 6.1, 6.5],
  "Humppila": [23.1, 40.1, 1.9, 8.8, 6.7, 8.6, 10.8],
  "Hyrynsalmi": [15.3, 28.3, 1.8, 7.7, 8.3, 9.1, 29.5],
  "Hyvinkää": [23.8, 37.5, 7.5, 12.4, 10.0, 8.8, 0.0],
  "Hämeenkyrö": [24.3, 44.3, 4.3, 5.7, 8.5, 7.6, 5.3],
  "Hämeenlinna": [22.1, 40.1, 0.4, 13.1, 12.1, 10.6, 1.6],
  "Ii": [23.7, 44.3, 5.9, 6.4, 6.8, 8.4, 4.5],
  "Iisalmi": [20.8, 37.6, 5.8, 14.2, 9.7, 11.7, 0.3],
  "Iitti": [21.4, 33.8, 8.2, 8.2, 11.0, 9.1, 8.4],
  "Ikaalinen": [18.2, 41.7, 3.9, 9.0, 13.8, 11.2, 2.4],
  "Ilmajoki": [27.1, 44.6, 6.0, 7.0, 6.8, 4.9, 3.7],
  "Ilomantsi": [13.6, 33.8, 7.8, 10.2, 5.3, 9.2, 20.1],
  "Imatra": [13.8, 31.5, 5.6, 12.5, 15.9, 13.7, 6.9],
  "Inari": [16.1, 34.6, 6.7, 10.3, 10.1, 11.1, 11.2],
  "Inkoo": [21.5, 45.5, 3.1, 10.0, 14.5, 5.4, 0.0],
  "Isojoki": [16.6, 39.4, 0.2, 11.0, 5.7, 9.1, 18.1],
  "Isokyrö": [18.4, 43.7, 10.6, 9.7, 7.1, 7.3, 3.2],
  "Janakkala": [25.8, 39.4, 5.3, 9.8, 8.2, 8.5, 3.1],
  "Joensuu": [21.9, 34.6, 7.6, 12.2, 10.7, 12.6, 0.4],
  "Jokioinen": [23.0, 48.4, 1.6, 9.5, 6.1, 6.8, 4.7],
  "Joroinen": [20.4, 36.7, 8.1, 7.3, 7.2, 7.2, 13.1],
  "Joutsa": [15.4, 37.8, 10.7, 8.3, 8.5, 7.7, 11.7],
  "Juuka": [15.0, 39.3, 10.4, 9.2, 8.4, 12.9, 4.9],
  "Juupajoki": [17.5, 47.7, 1.2, 6.7, 8.6, 5.3, 13.0],
  "Juva": [19.3, 35.0, 6.2, 11.8, 6.9, 7.0, 13.8],
  "Jyväskylä": [26.1, 36.2, 0.9, 13.4, 10.4, 12.9, 0.0],
  "Jämijärvi": [18.4, 42.0, 0.2, 6.9, 4.9, 6.0, 21.6],
  "Jämsä": [21.9, 41.3, 5.2, 8.4, 11.5, 11.7, 0.0],
  "Järvenpää": [26.4, 37.9, 7.0, 8.7, 11.4, 6.3, 2.3],
  "Kaarina": [32.4, 40.5, 4.1, 7.0, 8.9, 4.9, 2.3],
  "Kaavi": [21.9, 42.7, 0.8, 8.7, 8.9, 9.3, 7.7],
  "Kajaani": [19.0, 28.9, 22.4, 11.4, 7.6, 7.1, 3.7],
  "Kalajoki": [22.1, 40.4, 7.0, 12.9, 11.8, 5.8, 0.1],
  "Kangasala": [26.8, 39.4, 3.8, 9.1, 9.8, 7.4, 3.7],
  "Kangasniemi": [18.8, 35.6, 8.1, 8.7, 6.8, 8.2, 13.9],
  "Kankaanpää": [20.6, 41.4, 6.3, 8.4, 9.1, 6.3, 8.0],
  "Kannonkoski": [11.0, 35.3, 0.3, 12.1, 5.6, 9.5, 26.2],
  "Kannus": [18.9, 36.1, 5.9, 10.1, 8.8, 5.5, 14.7],
  "Karijoki": [22.9, 35.3, 0.8, 8.7, 8.7, 9.3, 14.3],
  "Karkkila": [19.4, 43.7, 9.0, 10.3, 7.8, 9.9, 0.0],
  "Karstula": [19.1, 32.9, 10.4, 8.3, 6.9, 9.8, 12.6],
  "Karvia": [21.5, 39.0, 0.4, 10.8, 6.6, 6.9, 14.8],
  "Kaskinen": [22.2, 29.2, 1.6, 10.1, 9.7, 0.8, 26.4],
  "Kauhajoki": [19.8, 40.7, 7.2, 9.9, 7.6, 9.3, 5.6],
  "Kauhava": [19.6, 44.3, 6.6, 11.2, 9.5, 4.4, 4.4],
  "Kauniainen": [21.4, 32.4, 17.7, 14.5, 11.0, 2.7, 0.4],
  "Kaustinen": [19.4, 36.7, 13.7, 6.2, 5.7, 6.8, 11.5],
  "Keitele": [18.9, 40.7, 1.3, 10.4, 13.3, 4.4, 10.9],
  "Kemi": [18.8, 34.2, 7.7, 17.7, 9.2, 12.5, 0.1],
  "Kemijärvi": [15.0, 21.2, 5.9, 8.1, 10.4, 10.0, 29.4],
  "Keminmaa": [18.8, 42.8, 6.7, 4.9, 8.2, 6.9, 11.7],
  "Kemiönsaari": [18.4, 40.6, 6.8, 11.6, 10.9, 8.7, 2.9],
  "Kempele": [28.2, 44.0, 6.4, 4.7, 8.3, 5.8, 2.6],
  "Kerava": [27.7, 34.6, 5.8, 8.9, 12.3, 10.3, 0.5],
  "Keuruu": [17.7, 44.0, 8.4, 11.0, 9.2, 9.7, 0.0],
  "Kihniö": [16.4, 39.2, 0.8, 10.8, 9.1, 8.8, 14.9],
  "Kinnula": [17.8, 31.9, 13.9, 6.6, 6.7, 6.7, 16.4],
  "Kirkkonummi": [26.6, 42.4, 6.1, 6.9, 8.6, 5.0, 4.5],
  "Kitee": [15.2, 37.3, 7.0, 10.1, 7.9, 9.3, 13.3],
  "Kittilä": [22.4, 34.8, 9.3, 13.2, 8.5, 6.5, 5.2],
  "Kiuruvesi": [25.3, 34.3, 7.6, 10.3, 7.2, 8.2, 7.1],
  "Kivijärvi": [11.1, 38.2, 1.1, 6.7, 6.9, 8.2, 27.8],
  "Kokemäki": [18.9, 37.5, 7.5, 7.2, 9.1, 9.7, 10.0],
  "Kokkola": [24.9, 37.6, 6.8, 10.0, 8.2, 7.7, 4.8],
  "Kolari": [17.6, 35.1, 6.6, 10.6, 7.1, 11.4, 11.6],
  "Konnevesi": [19.0, 36.3, 8.4, 8.9, 5.6, 8.3, 13.6],
  "Kontiolahti": [26.8, 45.2, 3.9, 7.2, 5.9, 7.7, 3.4],
  "Korsnäs": [25.2, 48.7, 2.3, 8.3, 7.9, 4.5, 3.1],
  "Koski Tl": [19.1, 38.7, 18.2, 9.4, 5.9, 7.7, 1.1],
  "Kotka": [20.4, 33.5, 5.7, 16.5, 11.0, 11.6, 1.2],
  "Kouvola": [22.5, 37.7, 6.3, 12.3, 11.6, 9.6, 0.1],
  "Kristiinankaupunki": [18.0, 37.4, 11.5, 9.8, 15.4, 3.8, 4.1],
  "Kruunupyy": [24.6, 40.8, 9.2, 5.3, 6.1, 4.5, 9.5],
  "Kuhmo": [15.3, 32.8, 5.8, 14.6, 10.9, 12.8, 7.9],
  "Kuhmoinen": [15.9, 34.6, 9.0, 8.5, 11.8, 6.0, 14.2],
  "Kuopio": [25.6, 32.5, 7.4, 14.6, 12.1, 7.8, 0.0],
  "Kuortane": [15.8, 33.8, 19.9, 6.7, 9.9, 4.2, 9.7],
  "Kurikka": [21.1, 41.0, 6.8, 12.6, 10.2, 7.7, 0.6],
  "Kustavi": [14.8, 30.6, 0.8, 11.5, 14.0, 6.8, 21.5],
  "Kuusamo": [16.0, 39.8, 7.0, 14.3, 12.1, 10.8, 0.0],
  "Kyyjärvi": [18.0, 47.6, 0.7, 8.6, 6.7, 6.8, 11.7],
  "Kärkölä": [19.5, 38.3, 0.5, 10.1, 10.2, 11.4, 10.0],
  "Kärsämäki": [25.2, 37.6, 7.9, 9.3, 4.4, 7.5, 8.2],
  "Lahti": [21.5, 32.1, 6.4, 11.8, 11.3, 16.9, 0.0],
  "Laihia": [18.6, 39.3, 5.1, 5.6, 11.3, 4.1, 16.0],
  "Laitila": [19.1, 46.8, 10.7, 10.8, 7.2, 4.6, 0.7],
  "Lapinjärvi": [24.2, 43.8, 1.1, 8.1, 6.2, 8.9, 7.7],
  "Lapinlahti": [20.1, 42.8, 6.7, 8.6, 9.0, 7.8, 4.9],
  "Lappajärvi": [16.8, 35.8, 10.3, 8.8, 5.7, 7.2, 15.4],
  "Lappeenranta": [19.8, 34.9, 5.2, 14.5, 10.5, 13.5, 1.5],
  "Lapua": [21.5, 39.8, 7.5, 15.1, 11.1, 4.9, 0.1],
  "Laukaa": [24.5, 50.9, 5.2, 6.9, 6.8, 5.7, 0.0],
  "Lemi": [23.9, 48.1, 0.4, 5.7, 4.4, 8.5, 9.0],
  "Lempäälä": [25.3, 43.0, 4.6, 9.5, 9.4, 8.3, 0.0],
  "Leppävirta": [19.4, 38.4, 5.1, 8.7, 8.0, 7.8, 12.6],
  "Lestijärvi": [17.8, 45.4, 0.5, 4.4, 8.5, 5.0, 18.4],
  "Lieksa": [13.0, 29.8, 4.2, 16.7, 9.9, 12.2, 14.2],
  "Lieto": [31.0, 43.9, 4.5, 6.2, 9.0, 5.4, 0.0],
  "Liminka": [24.4, 43.2, 7.1, 8.3, 6.2, 5.7, 5.2],
  "Liperi": [23.5, 47.4, 0.4, 8.1, 4.7, 11.2, 4.7],
  "Lohja": [23.7, 43.4, 7.2, 9.4, 9.2, 6.8, 0.3],
  "Loimaa": [24.9, 40.0, 2.3, 11.6, 9.4, 9.0, 2.8],
  "Loppi": [23.8, 45.6, 5.4, 8.4, 7.6, 6.5, 2.8],
  "Loviisa": [19.2, 40.0, 8.2, 9.7, 16.1, 6.9, 0.0],
  "Luhanka": [10.6, 28.9, 10.7, 9.7, 12.0, 5.1, 23.0],
  "Lumijoki": [26.2, 51.7, 0.3, 8.9, 4.1, 1.6, 7.3],
  "Luoto": [33.9, 50.0, 0.2, 7.2, 5.9, 2.8, 0.0],
  "Luumäki": [20.1, 39.7, 5.6, 12.3, 10.1, 8.7, 3.5],
  "Maalahti": [29.3, 42.0, 8.9, 6.7, 7.3, 3.6, 2.1],
  "Marttila": [19.2, 38.8, 0.5, 9.3, 7.5, 8.9, 15.8],
  "Masku": [28.1, 50.7, 0.0, 6.3, 6.8, 4.3, 3.7],
  "Merijärvi": [22.5, 49.1, 0.3, 9.5, 4.4, 3.3, 10.9],
  "Merikarvia": [16.9, 37.1, 14.2, 11.5, 9.0, 4.7, 6.6],
  "Miehikkälä": [17.1, 33.8, 0.3, 9.3, 10.3, 9.1, 20.1],
  "Mikkeli": [23.7, 36.3, 7.1, 10.6, 10.7, 10.2, 1.4],
  "Muhos": [22.2, 44.5, 6.8, 9.7, 9.2, 7.5, 0.0],
  "Multia": [20.3, 45.5, 0.4, 9.1, 7.7, 5.9, 11.2],
  "Muonio": [12.5, 33.4, 7.2, 9.9, 5.5, 7.1, 24.4],
  "Mustasaari": [28.5, 44.2, 4.7, 8.1, 7.1, 4.4, 3.0],
  "Muurame": [22.7, 39.5, 5.6, 7.8, 10.5, 6.4, 7.5],
  "Mynämäki": [23.9, 39.3, 7.1, 6.2, 7.6, 6.1, 9.8],
  "Myrskylä": [21.7, 42.4, 0.3, 6.6, 12.5, 10.1, 6.4],
  "Mäntsälä": [24.8, 47.7, 5.6, 5.5, 10.7, 5.7, 0.0],
  "Mänttä-Vilppula": [19.3, 36.4, 8.9, 11.8, 12.8, 7.3, 3.6],
  "Mäntyharju": [17.9, 40.0, 10.6, 8.2, 13.2, 10.1, 0.0],
  "Naantali": [22.4, 38.6, 6.1, 12.3, 14.6, 6.0, 0.0],
  "Nakkila": [22.0, 39.3, 7.2, 8.5, 7.0, 11.1, 4.9],
  "Nivala": [21.2, 49.0, 5.0, 9.3, 5.0, 6.5, 4.0],
  "Nokia": [26.9, 38.0, 5.6, 7.8, 11.5, 8.8, 1.3],
  "Nousiainen": [25.9, 44.6, 11.5, 7.8, 3.2, 2.8, 4.2],
  "Nurmes": [17.6, 32.9, 5.2, 11.6, 8.5, 9.5, 14.7],
  "Nurmijärvi": [28.5, 42.0, 2.4, 7.6, 11.0, 5.1, 3.3],
  "Närpiö": [24.5, 44.1, 7.1, 7.5, 10.1, 4.8, 1.8],
  "Orimattila": [22.8, 43.3, 6.1, 10.9, 9.3, 7.6, 0.0],
  "Oripää": [24.0, 47.0, 0.7, 10.8, 6.4, 3.6, 7.5],
  "Orivesi": [19.1, 33.7, 7.7, 7.5, 11.3, 8.3, 12.3],
  "Oulainen": [22.2, 41.0, 6.6, 11.1, 8.0, 6.4, 4.6],
  "Oulu": [23.9, 35.8, 7.2, 11.5, 9.5, 12.0, 0.1],
  "Outokumpu": [19.8, 37.6, 6.0, 13.5, 8.9, 14.2, 0.0],
  "Padasjoki": [15.1, 30.6, 7.2, 11.7, 7.3, 12.6, 15.5],
  "Paimio": [28.6, 38.3, 6.8, 7.9, 9.1, 4.8, 4.6],
  "Paltamo": [15.5, 39.3, 10.5, 7.7, 8.3, 10.0, 8.7],
  "Parainen": [21.3, 44.8, 7.3, 8.0, 11.5, 7.1, 0.0],
  "Parikkala": [17.6, 39.1, 10.5, 8.2, 11.4, 9.9, 3.2],
  "Parkano": [16.6, 33.6, 7.6, 6.5, 13.5, 9.7, 12.5],
  "Pedersören kunta": [29.1, 50.8, 5.2, 6.2, 5.9, 2.7, 0.0],
  "Pelkosenniemi": [17.8, 33.6, 0.7, 12.6, 11.2, 7.9, 16.1],
  "Pello": [15.7, 37.2, 9.5, 11.3, 6.6, 7.9, 11.9],
  "Perho": [21.3, 47.6, 6.3, 5.6, 2.8, 5.6, 10.9],
  "Petäjävesi": [23.3, 41.7, 9.3, 6.2, 5.7, 7.3, 6.5],
  "Pieksämäki": [21.6, 32.3, 5.3, 9.8, 11.1, 4.6, 15.2],
  "Pielavesi": [18.0, 44.4, 8.7, 6.7, 10.8, 7.9, 3.4],
  "Pietarsaari": [22.7, 36.9, 8.7, 12.3, 11.8, 7.2, 0.5],
  "Pihtipudas": [15.6, 36.4, 13.6, 9.2, 9.3, 7.8, 8.1],
  "Pirkkala": [25.8, 40.0, 3.5, 7.6, 11.2, 6.6, 5.2],
  "Polvijärvi": [19.0, 39.4, 7.3, 7.5, 3.9, 11.8, 11.3],
  "Pomarkku": [20.6, 37.0, 15.4, 8.0, 5.2, 6.3, 7.4],
  "Pori": [22.2, 32.8, 4.4, 13.5, 10.9, 12.0, 4.2],
  "Pornainen": [24.4, 47.4, 0.1, 4.9, 7.0, 5.9, 10.3],
  "Porvoo": [23.1, 37.3, 6.7, 12.5, 10.5, 6.9, 2.9],
  "Posio": [12.6, 23.9, 10.0, 8.6, 5.4, 8.2, 31.4],
  "Pudasjärvi": [12.0, 36.1, 5.8, 10.4, 11.3, 9.3, 15.2],
  "Pukkila": [19.6, 40.1, 0.1, 7.3, 6.2, 10.8, 16.0],
  "Punkalaidun": [15.0, 44.4, 10.6, 9.0, 5.2, 6.3, 9.5],
  "Puolanka": [9.2, 39.0, 9.1, 8.0, 10.8, 12.0, 12.0],
  "Puumala": [14.8, 33.1, 4.0, 13.0, 15.0, 12.2, 7.8],
  "Pyhtää": [24.1, 37.6, 0.3, 11.5, 8.4, 9.4, 8.7],
  "Pyhäjoki": [18.5, 34.9, 10.5, 9.3, 9.5, 6.7, 10.6],
  "Pyhäjärvi": [21.5, 37.4, 7.4, 11.1, 8.0, 8.8, 5.8],
  "Pyhäntä": [22.6, 42.6, 0.9, 8.3, 8.4, 1.0, 16.3],
  "Pyhäranta": [20.2, 45.2, 1.1, 4.8, 5.8, 6.3, 16.6],
  "Pälkäne": [22.4, 39.1, 6.1, 8.7, 10.1, 8.5, 5.0],
  "Pöytyä": [20.5, 53.2, 7.9, 7.7, 5.0, 5.8, 0.0],
  "Raahe": [17.9, 39.2, 4.7, 12.1, 11.3, 10.1, 4.6],
  "Raasepori": [23.7, 41.6, 6.7, 10.5, 10.4, 7.2, 0.0],
  "Raisio": [29.3, 35.8, 5.9, 8.7, 12.0, 5.1, 3.2],
  "Rantasalmi": [16.2, 38.4, 9.0, 9.3, 10.2, 6.9, 9.9],
  "Ranua": [16.9, 44.4, 8.2, 7.7, 10.3, 6.3, 6.2],
  "Rauma": [20.8, 37.9, 6.0, 15.1, 11.5, 8.7, 0.0],
  "Rautalampi": [16.0, 32.6, 14.5, 8.1, 10.9, 5.4, 12.5],
  "Rautavaara": [11.2, 31.8, 13.9, 9.4, 8.5, 8.1, 17.1],
  "Rautjärvi": [14.1, 32.9, 8.1, 10.6, 6.9, 11.8, 15.7],
  "Reisjärvi": [20.7, 43.2, 7.4, 6.7, 6.7, 5.2, 10.1],
  "Riihimäki": [18.2, 29.0, 5.6, 12.3, 14.8, 7.6, 12.5],
  "Ristijärvi": [16.3, 38.3, 0.6, 9.6, 5.3, 8.1, 21.7],
  "Rovaniemi": [30.1, 36.7, 4.7, 12.5, 7.9, 8.2, 0.0],
  "Ruokolahti": [17.0, 41.1, 1.4, 8.6, 9.8, 11.2, 10.9],
  "Ruovesi": [14.6, 35.3, 4.0, 7.2, 7.5, 11.5, 19.9],
  "Rusko": [32.8, 44.1, 0.0, 6.3, 6.0, 3.3, 7.6],
  "Rääkkylä": [9.8, 34.4, 0.9, 12.1, 6.4, 16.8, 19.6],
  "Saarijärvi": [15.9, 37.3, 8.5, 9.5, 6.9, 11.2, 10.7],
  "Salla": [12.2, 28.1, 11.5, 11.7, 10.7, 11.0, 14.8],
  "Salo": [22.8, 39.1, 6.9, 11.1, 8.4, 10.7, 0.9],
  "Sastamala": [22.7, 42.1, 5.9, 9.6, 11.6, 7.0, 1.2],
  "Sauvo": [27.8, 41.4, 0.3, 5.6, 7.5, 3.5, 14.1],
  "Savitaipale": [19.2, 30.1, 14.6, 9.1, 9.5, 6.6, 10.9],
  "Savonlinna": [18.2, 33.0, 7.7, 10.3, 11.6, 12.7, 6.5],
  "Savukoski": [16.8, 32.2, 14.8, 12.7, 3.9, 10.9, 8.8],
  "Seinäjoki": [27.5, 33.8, 7.2, 11.4, 10.4, 7.4, 2.3],
  "Sievi": [18.5, 43.3, 5.5, 7.5, 11.8, 7.0, 6.3],
  "Siikainen": [16.4, 44.3, 1.1, 10.0, 9.0, 10.1, 9.0],
  "Siikajoki": [20.5, 48.0, 6.5, 5.5, 6.1, 7.0, 6.3],
  "Siikalatva": [20.6, 42.5, 6.3, 7.6, 4.7, 8.5, 9.8],
  "Siilinjärvi": [27.4, 40.9, 4.6, 8.9, 9.3, 4.7, 4.2],
  "Simo": [14.6, 36.3, 8.7, 6.6, 9.8, 6.9, 17.2],
  "Sipoo": [25.7, 40.0, 6.1, 8.2, 10.9, 5.6, 3.4],
  "Siuntio": [23.4, 42.1, 0.5, 11.4, 8.3, 7.9, 6.4],
  "Sodankylä": [20.5, 35.5, 6.7, 14.7, 10.6, 8.8, 3.3],
  "Soini": [17.5, 40.1, 0.3, 10.2, 14.3, 7.7, 9.9],
  "Somero": [21.4, 35.4, 8.7, 13.1, 8.2, 6.0, 7.1],
  "Sonkajärvi": [19.0, 33.1, 9.1, 11.2, 7.1, 8.6, 12.0],
  "Sotkamo": [21.9, 33.8, 10.5, 8.4, 10.0, 7.0, 8.4],
  "Sulkava": [13.4, 35.8, 14.7, 9.1, 8.6, 10.0, 8.2],
  "Suomussalmi": [14.9, 39.5, 7.3, 11.2, 11.7, 9.7, 5.7],
  "Suonenjoki": [18.6, 35.0, 9.8, 14.2, 9.4, 5.8, 7.2],
  "Sysmä": [9.4, 25.1, 7.1, 8.8, 13.9, 8.6, 27.0],
  "Säkylä": [22.2, 42.9, 8.9, 7.2, 10.0, 5.9, 2.8],
  "Taipalsaari": [21.7, 42.8, 0.1, 8.4, 9.3, 9.8, 7.8],
  "Taivalkoski": [15.4, 33.6, 6.1, 8.3, 9.8, 7.9, 18.9],
  "Taivassalo": [22.5, 42.7, 0.6, 6.6, 5.3, 2.5, 19.9],
  "Tammela": [27.8, 44.1, 0.6, 9.2, 6.1, 7.0, 5.1],
  "Tampere": [19.9, 25.5, 15.6, 13.6, 12.6, 12.8, 0.0],
  "Tervo": [13.2, 37.1, 1.0, 12.2, 10.2, 9.7, 16.7],
  "Tervola": [19.1, 38.2, 6.1, 8.7, 8.3, 7.2, 12.3],
  "Teuva": [21.4, 41.2, 5.6, 6.9, 6.5, 8.3, 10.1],
  "Tohmajärvi": [14.3, 35.4, 6.7, 15.7, 5.7, 13.2, 9.1],
  "Toholampi": [19.3, 44.2, 7.7, 10.7, 5.3, 7.3, 5.4],
  "Toivakka": [24.9, 46.8, 1.6, 5.5, 4.0, 8.1, 9.1],
  "Tornio": [21.0, 41.8, 5.2, 9.9, 9.4, 7.8, 4.8],
  "Turku": [22.4, 27.3, 14.8, 12.9, 9.4, 11.8, 1.4],
  "Tuusniemi": [16.0, 34.0, 11.1, 9.0, 9.5, 9.8, 10.7],
  "Tuusula": [25.4, 38.7, 6.3, 6.8, 15.1, 4.9, 2.9],
  "Tyrnävä": [28.1, 51.6, 1.1, 5.0, 7.8, 0.9, 5.5],
  "Ulvila": [24.5, 42.2, 6.3, 7.9, 8.3, 6.8, 4.0],
  "Urjala": [18.0, 37.5, 8.7, 6.4, 8.7, 13.9, 6.8],
  "Utajärvi": [20.0, 31.0, 8.0, 9.3, 8.4, 10.1, 13.2],
  "Utsjoki": [11.0, 43.4, 9.5, 12.9, 3.4, 8.2, 11.5],
  "Uurainen": [27.3, 48.4, 0.0, 5.2, 6.5, 6.8, 5.8],
  "Uusikaarlepyy": [24.6, 40.5, 7.1, 6.8, 12.4, 4.2, 4.4],
  "Uusikaupunki": [24.1, 36.5, 5.2, 14.2, 8.2, 8.6, 3.2],
  "Vaala": [11.8, 28.0, 9.0, 8.8, 18.6, 9.3, 14.5],
  "Vaasa": [22.0, 32.0, 17.5, 13.0, 9.4, 6.1, 0.0],
  "Valkeakoski": [22.5, 39.8, 9.1, 13.9, 7.1, 7.6, 0.0],
  "Vantaa": [27.1, 31.8, 10.4, 10.0, 10.8, 9.9, 0.0],
  "Varkaus": [21.7, 36.3, 2.0, 15.5, 11.1, 8.9, 4.4],
  "Vehmaa": [28.2, 42.1, 0.7, 9.8, 4.3, 6.3, 8.6],
  "Vesanto": [11.9, 37.1, 17.5, 12.5, 6.6, 7.7, 6.6],
  "Vesilahti": [31.2, 44.1, 0.3, 5.5, 6.0, 8.3, 4.6],
  "Veteli": [18.8, 41.3, 10.7, 10.6, 5.6, 6.2, 6.7],
  "Vieremä": [17.2, 42.7, 7.7, 7.8, 8.0, 7.7, 8.9],
  "Vihti": [26.4, 47.0, 5.9, 5.8, 8.6, 6.3, 0.0],
  "Viitasaari": [18.9, 32.6, 6.7, 13.0, 8.8, 12.8, 7.2],
  "Vimpeli": [14.9, 43.0, 2.4, 9.6, 7.5, 7.6, 15.0],
  "Virolahti": [14.4, 33.9, 9.5, 8.3, 12.8, 10.3, 10.9],
  "Virrat": [18.4, 32.7, 7.2, 14.2, 13.2, 9.1, 5.2],
  "Vöyri": [20.5, 37.6, 9.9, 6.9, 5.4, 2.0, 17.7],
  "Ylitornio": [19.5, 34.8, 3.9, 15.3, 7.2, 9.3, 10.1],
  "Ylivieska": [23.3, 45.7, 6.2, 10.5, 8.2, 6.2, 0.0],
  "Ylöjärvi": [28.0, 44.2, 4.2, 7.8, 7.9, 7.8, 0.0],
  "Ypäjä": [17.4, 42.8, 1.8, 10.0, 9.3, 8.5, 10.1],
  "Ähtäri": [14.1, 27.9, 7.9, 10.7, 7.6, 22.0, 9.7],
  "Äänekoski": [20.4, 38.9, 5.8, 12.7, 9.9, 8.5, 3.8],
};

export function municipalSpendingProfile(municipality: string | undefined): Record<MunicipalSpendingKey, number> {
  const row = municipality ? MUNICIPAL_SPENDING_2025[municipality] : undefined;
  if (!row) return MUNICIPAL_SPENDING_NATIONAL_2025;
  return Object.fromEntries(MUNICIPAL_SPENDING_KEYS.map((k, i) => [k, row[i] ?? 0])) as Record<MunicipalSpendingKey, number>;
}
